// Generic SSE (Server-Sent Events) connection utility.
// Auth-agnostic: callers provide the token. Domain-specific URL building
// and event handling belong in consuming projects.

import { getBackendUrl } from './api.js';

/**
 * @typedef {object} SSEConnectionOptions
 * @property {(data: any) => void} onEvent - Called with parsed JSON for each message
 * @property {() => void} [onConnected] - Called when connection opens
 * @property {() => void} [onDisconnected] - Called on unintentional disconnect
 * @property {(error: Error) => void} [onError] - Called on connection error
 * @property {string} [baseUrl] - Override the API base URL (defaults to the
 *   assets API, getBackendUrl).
 */

/**
 * @typedef {object} SSEConnection
 * @property {boolean} isConnected
 * @property {string | null} connectionError
 * @property {(token: string, params?: Record<string, string>) => void} connect
 * @property {() => void} disconnect
 * @property {(token: string, params?: Record<string, string>) => void} reconnect
 */

/**
 * Create an SSE connection that authenticates via a `token` query parameter.
 *
 * @param {string} url - The SSE endpoint path (e.g. `/api/games/123/events-stream`)
 * @param {SSEConnectionOptions} options
 * @returns {SSEConnection}
 */
export function createSSEConnection(url, options) {
    /** @type {EventSource | null} */
    let eventSource = null;
    let connected = false;
    /** @type {string | null} */
    let error = null;
    let intentionalDisconnect = false;

    /**
     * Open the SSE connection.
     * @param {string} token - Bearer token (PAT or JWT)
     * @param {Record<string, string>} [params] - Additional query parameters (e.g. { since: '42' })
     */
    function connect(token, params) {
        if (!token) {
            error = 'No access token available';
            console.warn('SSE: no access token');
            return;
        }

        if (eventSource) {
            console.warn('SSE: already connected');
            return;
        }

        intentionalDisconnect = false;

        const baseUrl = options.baseUrl ?? getBackendUrl();
        let fullUrl = `${baseUrl}${url}?token=${encodeURIComponent(token)}`;
        if (params) {
            for (const [key, value] of Object.entries(params)) {
                fullUrl += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            }
        }

        try {
            eventSource = new EventSource(fullUrl);

            eventSource.onopen = () => {
                connected = true;
                error = null;
                options.onConnected?.();
            };

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    options.onEvent(data);
                } catch (err) {
                    console.error('SSE: failed to parse message', err);
                }
            };

            eventSource.onerror = () => {
                if (intentionalDisconnect) return;

                connected = false;

                if (eventSource?.readyState === EventSource.CLOSED) {
                    error = 'Connection closed by server';
                    options.onError?.(new Error(error));
                    options.onDisconnected?.();
                    disconnect();
                } else {
                    error = 'Connection error';
                    options.onError?.(new Error(error));
                }
            };
        } catch (err) {
            error = /** @type {Error} */ (err).message;
            options.onError?.(/** @type {Error} */ (err));
        }
    }

    function disconnect() {
        if (eventSource) {
            intentionalDisconnect = true;
            eventSource.close();
            eventSource = null;
            connected = false;
        }
    }

    /**
     * Disconnect and reconnect after a short delay.
     * @param {string} token
     * @param {Record<string, string>} [params]
     */
    function reconnect(token, params) {
        disconnect();
        setTimeout(() => connect(token, params), 100);
    }

    return {
        get isConnected() {
            return connected;
        },
        get connectionError() {
            return error;
        },
        connect,
        disconnect,
        reconnect,
    };
}
